import { fork, put, select, takeEvery } from 'typed-redux-saga/macro';

import {
  APPLY_LEXICONS_TO_OPEN_DOCUMENT,
  ASSIGN_CODE,
  CLEAR_BATES_STAMP,
  CREATE_MANUAL_REDACTION,
  CREATE_MANUAL_REDACTIONS,
  CREATE_OR_MERGE_TEXT_REDACTIONS,
  DELETE_REDACTION,
  DELETE_REDACTIONS,
  DELETE_REDACTIONS_BY_PHRASE,
  EXPORT_REQUEST,
  LEXICON_APPLY_FAILURE,
  LEXICON_APPLY_SUCCESS,
  LOG_AUDIT_EVENT,
  MOVE_REDACTION,
  OPEN_DOCUMENT_SUCCESS,
  RESIZE_REDACTION,
  SAVE_SUCCESS,
  SET_BATES_STAMP,
  SET_DETECTIONS_REDACTED,
  SET_REDACTIONS_REDACTED,
  TOGGLE_DETECTION,
  TOGGLE_DETECTION_PAGE,
  TOGGLE_PAGE_EXCLUDE,
  TOGGLE_PAGE_REDACT,
  REDACT_PAGE_ELEMENTS,
  TOGGLE_REDACTION_ALL_PAGES,
  UPDATE_REDACTION_POINTS,
} from '../actions';
import {
  BatesStampSettings,
  BoundingBox,
  Detection,
  Redaction,
  SessionState,
  isLexiconDetection,
  runLeftWorkUnfinished,
} from '../models';
import {
  batesRangeForExport,
  describeBatesStamp,
  formatBatesLabel,
} from '../batesLabel';
import { countRedactionOccurrences } from '../helpers';
import { LICENSE_METER_ACTION, licenseMeterPayload } from '../licenseMeter';
import { formatPageRanges } from '../pageRanges';
import { selectLexiconApply, selectSession } from '../selectors';

/**
 * Records audit-log events for document-editor actions that affect the output
 * document — the document-mode counterpart to video's
 * `mediaDetails/saga/audit-logs.ts`.
 *
 * Events are emitted through the document-local `LOG_AUDIT_EVENT` action (→
 * `auditEmitSaga` → `serviceEmitAuditEvent`, under video's `'redact'` audit
 * application so they're queryable by tdoId — see AUDIT_APPLICATION in
 * services.ts), which stamps the live session's `tdoId`. The shared
 * `actionLogAuditEvent` path video uses is NOT viable here: it reads
 * `mediaDetails.tdo`, which the document route never populates, so it bails out
 * and emits nothing. On burn-in, `exportPush` gathers these events into a CSV
 * `redact-audit-log` asset (mirroring `imageRedactionSaga`).
 *
 * Coordinates in the DETAILS column are PDF user-space units (the geometry's
 * native space — i.e. document units), rounded to 3 decimals.
 *
 * Most watchers here log a REDACTION, and recover the pre-change value from the
 * undo snapshot the reducer just pushed (`prevRedactions`). The Bates ones
 * (VE-24447) log an identifier instead — the sequence a production is cited by
 * — so they record the whole definition rather than the fact that it changed,
 * and the export event carries the realised range.
 *
 * They deliberately do NOT use the undo snapshot: the reducer no-ops an
 * unchanged commit and pushes nothing, so the top snapshot says nothing about
 * the commit that just happened, and reading it made a second Apply re-log an
 * application that had not occurred. The pre-commit stamp rides on the action
 * instead — read from live state at the dispatch site, where it cannot go stale
 * — which is also why this saga holds no state of its own. Per-commit logging
 * is safe because the panel previews through a slice field these actions never
 * see; the volume problem that switched save logging off does not arise.
 *
 * EVERY EVENT HERE IS ABOUT THE OPEN FILE, which is what makes the session
 * fallback in `auditEmitSaga` safe. A merged document's set-wide acts — its
 * Bates stamping and its watermarks — used to be watched here too, and they
 * are not about the open file: a project URL resolves to its host member, so
 * those events were filed against `project.members[0]` and read back as that
 * one file's own doing. They live in `projects/auditEvents.ts` now, where the
 * project TDO is named explicitly (VE-31977).
 *
 * One event here is NOT for the customer: `watchDocumentOpened` records a
 * MuPDF license-meter event per document opened with a PDF (see
 * `../licenseMeter.ts`), which `uploadDocumentAuditLog` filters out of the CSV.
 */
export function* auditLogsSaga() {
  yield* fork(watchCreateManualRedaction);
  yield* fork(watchCreateManualRedactions);
  yield* fork(watchCreateOrMergeTextRedactions);
  yield* fork(watchMoveRedaction);
  yield* fork(watchResizeRedaction);
  yield* fork(watchUpdateRedactionPoints);
  yield* fork(watchDeleteRedaction);
  yield* fork(watchDeleteRedactions);
  yield* fork(watchDeleteRedactionsByPhrase);
  yield* fork(watchTogglePageRedact);
  yield* fork(watchTogglePageExclude);
  yield* fork(watchRedactPageElements);
  yield* fork(watchToggleDetection);
  yield* fork(watchToggleDetectionPage);
  yield* fork(watchSetDetectionsRedacted);
  yield* fork(watchAssignCode);
  yield* fork(watchToggleRedactionAllPages);
  yield* fork(watchSetRedactionsRedacted);
  yield* fork(watchSetBatesStamp);
  yield* fork(watchClearBatesStamp);
  yield* fork(watchDocumentExportRequested);
  yield* fork(watchDocumentSaved);
  yield* fork(watchLexiconApplyRequested);
  yield* fork(watchLexiconApplied);
  yield* fork(watchLexiconApplyFailed);
  yield* fork(watchDocumentOpened);
}

/**
 * Save logging is high-volume (auto-save fires on an interval), so it's off by
 * default — flip to `true` only when debugging the persistence path.
 */
const AUDIT_LOG_SAVES = false;

// ---------------------------------------------------------------------------
// Coordinate / phrase formatting
// ---------------------------------------------------------------------------

const f = (n: number): string => n.toFixed(3);

/** Bounding box in PDF user-space units, 3-decimal precision. */
function fmtBox(g: BoundingBox): string {
  return `x=${f(g.x)}, y=${f(g.y)}, w=${f(g.width)}, h=${f(g.height)}`;
}

function fmtPoints(points: Array<{ x: number; y: number }>): string {
  return points.map((p) => `(${f(p.x)},${f(p.y)})`).join(' ');
}

/**
 * Long phrases get collapsed to "first 10 words … last 5 words" so a redacted
 * paragraph doesn't dump its whole text into the audit log.
 */
function truncatePhrase(phrase: string): string {
  const words = phrase.trim().split(/\s+/);
  if (words.length <= 15) {
    return phrase.trim();
  }
  return `${words.slice(0, 10).join(' ')} … ${words.slice(-5).join(' ')}`;
}

interface AuditEntry {
  action: string;
  details?: string;
}

function* emit(entry: AuditEntry | null) {
  if (entry?.action) {
    yield* put(
      LOG_AUDIT_EVENT({
        action: entry.action,
        payload: entry.details ? { details: entry.details } : undefined,
      })
    );
  }
}

function redactionById(list: Redaction[], id: string): Redaction | undefined {
  return list.find((r) => r.id === id);
}

/** Pre-change redactions, recovered from the snapshot the reducer just pushed. */
function prevRedactions(session: SessionState | null | undefined): Redaction[] {
  const past = session?.undoStack?.past;
  return past && past.length > 0
    ? (past[past.length - 1]!.redactions ?? [])
    : [];
}

// ---------------------------------------------------------------------------
// Message builders
// ---------------------------------------------------------------------------

/** Label + coords for a single newly-created redaction. */
function describeRedaction(r: Redaction): AuditEntry | null {
  const page = `page ${r.page}`;
  switch (r.origin) {
    case 'keyword':
      return {
        action: `Redacted keyword match "${truncatePhrase(r.phrase ?? '')}" (${page})`,
      };
    case 'text-select':
      return {
        action: r.phrase
          ? `Redacted selected text "${truncatePhrase(r.phrase)}" (${page})`
          : `Redacted selected text (${page})`,
      };
    case 'page-redact':
      // Page-level redactions are logged from TOGGLE_PAGE_REDACT instead.
      return null;
    case 'manual':
    default:
      // A custom polygon is a `manual` redaction carrying vertices.
      if (r.isCustomPolygon && r.points && r.points.length >= 3) {
        return {
          action: `Added polygon redaction (${page})`,
          details: `points (pdf pts): ${fmtPoints(r.points)}`,
        };
      }
      return {
        action: `Added redaction (${page})`,
        details: `box (pdf pts): ${fmtBox(r.geometry)}`,
      };
  }
}

/**
 * Label for a bulk create. Collapses a "Redact all matches" batch (every box
 * sharing one phrase) into a single summary line; otherwise falls back to the
 * single-redaction label or a generic count.
 */
function describeBulk(redactions: Redaction[]): AuditEntry | null {
  const created = redactions.filter((r) => r.origin !== 'page-redact');
  if (created.length === 0) {
    return null;
  }
  if (created.length === 1) {
    return describeRedaction(created[0]!);
  }
  // Only collapse into a "Redacted all N matches" line when EVERY created box
  // carries the same non-empty phrase — otherwise N would overcount unphrased
  // boxes mixed into the batch.
  const trimmed = created.map((r) => (r.phrase ?? '').trim());
  if (trimmed.every(Boolean) && new Set(trimmed).size === 1) {
    // N counts OCCURRENCES: "Redacted all 3 matches" for two wrapped addresses
    // would misstate the record (VE-26267).
    const occurrences = countRedactionOccurrences(created);
    // ONE occurrence that happens to span several row boxes — a match or
    // selection sitting on a line break — is not a "redact all": log it as the
    // single redaction it is, rather than "Redacted all 1 matches" (VE-26267).
    if (occurrences === 1) {
      return describeRedaction(created[0]!);
    }
    return {
      action: `Redacted all ${occurrences} matches of "${truncatePhrase(
        trimmed[0]!
      )}"`,
    };
  }
  return { action: `Added ${created.length} redactions` };
}

// ---------------------------------------------------------------------------
// Create watchers
// ---------------------------------------------------------------------------

function* watchCreateManualRedaction() {
  yield* takeEvery(CREATE_MANUAL_REDACTION, function* ({ payload }) {
    yield* emit(describeRedaction(payload.redaction));
  });
}

function* watchCreateManualRedactions() {
  yield* takeEvery(CREATE_MANUAL_REDACTIONS, function* ({ payload }) {
    yield* emit(describeBulk(payload.redactions));
  });
}

function* watchCreateOrMergeTextRedactions() {
  yield* takeEvery(CREATE_OR_MERGE_TEXT_REDACTIONS, function* ({ payload }) {
    yield* emit(describeBulk(payload.redactions));
  });
}

// ---------------------------------------------------------------------------
// Geometry-edit watchers (fire once on drag-end; original geometry comes from
// the pre-change undo snapshot, new geometry from current state)
// ---------------------------------------------------------------------------

function* watchMoveRedaction() {
  yield* takeEvery(MOVE_REDACTION, function* ({ payload }) {
    const session = yield* select(selectSession);
    const next = redactionById(session?.redactions ?? [], payload.id);
    const prev = redactionById(prevRedactions(session), payload.id);
    if (!next || !prev) {
      return;
    }
    yield* emit({
      action: `Moved redaction (page ${next.page})`,
      details: `from (pdf pts): ${fmtBox(prev.geometry)} → to: ${fmtBox(
        next.geometry
      )}`,
    });
  });
}

function* watchResizeRedaction() {
  yield* takeEvery(RESIZE_REDACTION, function* ({ payload }) {
    const session = yield* select(selectSession);
    const next = redactionById(session?.redactions ?? [], payload.id);
    const prev = redactionById(prevRedactions(session), payload.id);
    if (!next || !prev) {
      return;
    }
    yield* emit({
      action: `Resized redaction (page ${next.page})`,
      details: `from (pdf pts): ${fmtBox(prev.geometry)} → to: ${fmtBox(
        next.geometry
      )}`,
    });
  });
}

function* watchUpdateRedactionPoints() {
  yield* takeEvery(UPDATE_REDACTION_POINTS, function* ({ payload }) {
    const session = yield* select(selectSession);
    const next = redactionById(session?.redactions ?? [], payload.id);
    const prev = redactionById(prevRedactions(session), payload.id);
    if (!next?.points || !prev?.points) {
      return;
    }
    yield* emit({
      action: `Edited polygon redaction (page ${next.page})`,
      details: `from (pdf pts): ${fmtPoints(prev.points)} → to: ${fmtPoints(
        next.points
      )}`,
    });
  });
}

// ---------------------------------------------------------------------------
// Delete watchers (deleted redactions are recovered from the undo snapshot)
// ---------------------------------------------------------------------------

function* watchDeleteRedaction() {
  yield* takeEvery(DELETE_REDACTION, function* ({ payload }) {
    const session = yield* select(selectSession);
    const deleted = redactionById(prevRedactions(session), payload.id);
    yield* emit({
      action: deleted
        ? `Deleted redaction (page ${deleted.page})`
        : 'Deleted redaction',
      details: deleted
        ? `box (pdf pts): ${fmtBox(deleted.geometry)}`
        : undefined,
    });
  });
}

function* watchDeleteRedactions() {
  yield* takeEvery(DELETE_REDACTIONS, function* ({ payload }) {
    // Reducer no-ops on an empty id set (no snapshot pushed) — don't log it.
    if (payload.ids.length === 0) {
      return;
    }
    const session = yield* select(selectSession);
    const prev = prevRedactions(session);
    const deleted = payload.ids
      .map((id) => redactionById(prev, id))
      .filter((r): r is Redaction => !!r);
    const locations = deleted
      .map((r) => `page ${r.page}: ${fmtBox(r.geometry)}`)
      .join('; ');
    yield* emit({
      action: `Deleted ${payload.ids.length} redactions`,
      details: locations ? `(pdf pts) ${locations}` : undefined,
    });
  });
}

function* watchDeleteRedactionsByPhrase() {
  yield* takeEvery(DELETE_REDACTIONS_BY_PHRASE, function* ({ payload }) {
    // Reducer no-ops on a blank phrase — don't log a deletion that didn't run.
    if (!payload.phrase.trim()) {
      return;
    }
    yield* emit({
      action: `Unredact all matches of "${truncatePhrase(payload.phrase)}"`,
    });
  });
}

// ---------------------------------------------------------------------------
// Page-level watchers
// ---------------------------------------------------------------------------

function* watchTogglePageRedact() {
  yield* takeEvery(TOGGLE_PAGE_REDACT, function* ({ payload }) {
    // Saga middleware runs after the reducer, so this reflects the
    // post-toggle state — letting us log whether the page is now removed.
    const session = yield* select(selectSession);
    const on = !!session?.pageRedacts.includes(payload.page);
    yield* emit({
      action: on
        ? `Removed page ${payload.page}`
        : `Restored page ${payload.page}`,
    });
  });
}

function* watchTogglePageExclude() {
  yield* takeEvery(TOGGLE_PAGE_EXCLUDE, function* ({ payload }) {
    const session = yield* select(selectSession);
    const on = !!session?.excludedPages.includes(payload.page);
    yield* emit({
      action: on
        ? `Deleted page ${payload.page} from output`
        : `Restored page ${payload.page} to output`,
    });
  });
}

/**
 * Redact Page (VE-25621): ONE event per click, however many boxes it added —
 * not CREATE_MANUAL_REDACTIONS' "Added N redactions". Counts what the reducer
 * actually kept after dedupe (the boxes share a `pageRedactionId`), and logs
 * nothing when the page was already fully redacted.
 */
function* watchRedactPageElements() {
  yield* takeEvery(REDACT_PAGE_ELEMENTS, function* ({ payload }) {
    const groupId = payload.redactions[0]?.pageRedactionId;
    if (!groupId) {
      return;
    }
    const session = yield* select(selectSession);
    const added =
      session?.redactions.filter((r) => r.pageRedactionId === groupId).length ??
      0;
    if (added === 0) {
      return;
    }
    yield* emit({
      action: `Redacted page ${payload.page} (${added} ${
        added === 1 ? 'redaction' : 'redactions'
      })`,
    });
  });
}

// ---------------------------------------------------------------------------
// PII detection watchers
// ---------------------------------------------------------------------------

/**
 * How a detection may be named in the document audit CSV.
 *
 * ── NFR-S7 ───────────────────────────────────────────────────────────────────
 *
 * A lexicon detection's `label` IS the entry term, and the CSV is readable by
 * everyone with document access. NFR-S7 is unconditional: lexicon activity is
 * identified by LEXICON NAME, never by term. So this must be used instead of
 * `det.label` everywhere a detection is named in this file — a plain
 * `${det.label}` on a lexicon row silently exports the agency's term list.
 *
 * PII rows keep their existing label. That is not an inconsistency: the PII
 * label is engine output the CSV has always carried, and NFR-S7's second
 * sentence is "do not add removed text to an artifact that does not already
 * carry it", not "retro-scrub PII" (that is `R10`, raised separately).
 */
function auditLabelOf(detection: Detection): string {
  return isLexiconDetection(detection)
    ? `lexicon "${detection.lexicon.lexiconName}"`
    : `"${detection.label}"`;
}

function* watchToggleDetection() {
  yield* takeEvery(TOGGLE_DETECTION, function* ({ payload }) {
    const session = yield* select(selectSession);
    const det = session?.detections.find((d) => d.id === payload.detectionId);
    // Unknown id → reducer no-op; skip rather than log a phantom event that
    // also leaks the raw detection id as the label.
    if (!det) {
      return;
    }
    // `checked` is true | 'partial' | false after the toggle; anything other
    // than an explicit `false` means at least one instance is now redacted.
    const on = det.checked !== false;
    const name = auditLabelOf(det);
    yield* emit({
      action: on
        ? `Redacted all instances of ${name}`
        : `Unredact all instances of ${name}`,
    });
  });
}

function* watchToggleDetectionPage() {
  yield* takeEvery(TOGGLE_DETECTION_PAGE, function* ({ payload }) {
    const session = yield* select(selectSession);
    const det = session?.detections.find((d) => d.id === payload.detectionId);
    if (!det) {
      return;
    }
    yield* emit({
      action: `Toggled ${auditLabelOf(det)} redaction on page ${payload.page}`,
    });
  });
}

/** Names listed in a bulk detection event's DETAILS before "and N more". */
const BULK_DETECTION_NAMES_LISTED = 10;

/**
 * The bulk detection actions: the multi-select bar's Redact / Unredact and the
 * Detections tab's "Add all", which can take a three-hundred-row list in one
 * click. ONE event per dispatch, like `describeBulk` and
 * `watchSetRedactionsRedacted` — every event is its own network call, so one
 * per detection fanned "Add all" out into hundreds of requests (VE-30158
 * review). A single detection keeps TOGGLE_DETECTION's wording, since it is
 * the same whole-card change; more name the count in ACTION and list the
 * names in DETAILS, capped. Names go through `auditLabelOf` (NFR-S7).
 *
 * The target state is on the action, so it is logged as asked rather than read
 * back. Ids that no longer exist are skipped, as the reducer skips them.
 */
function* watchSetDetectionsRedacted() {
  yield* takeEvery(SET_DETECTIONS_REDACTED, function* ({ payload }) {
    const session = yield* select(selectSession);
    const byId = new Map((session?.detections ?? []).map((d) => [d.id, d]));
    const names = payload.detectionIds.flatMap((id) => {
      const det = byId.get(id);
      return det ? [auditLabelOf(det)] : [];
    });
    if (names.length === 0) {
      return;
    }
    const verb = payload.redacted ? 'Redacted' : 'Unredact';
    if (names.length === 1) {
      yield* emit({ action: `${verb} all instances of ${names[0]}` });
      return;
    }
    const listed = names.slice(0, BULK_DETECTION_NAMES_LISTED).join(', ');
    const rest = names.length - BULK_DETECTION_NAMES_LISTED;
    yield* emit({
      action: `${verb} all instances of ${names.length} detections`,
      details: rest > 0 ? `${listed}, and ${rest} more` : listed,
    });
  });
}

// ---------------------------------------------------------------------------
// Code watchers
// ---------------------------------------------------------------------------

function* watchAssignCode() {
  yield* takeEvery(ASSIGN_CODE, function* ({ payload }) {
    const session = yield* select(selectSession);
    let details: string | undefined;
    if (payload.targetType === 'redaction') {
      const r = redactionById(session?.redactions ?? [], payload.targetId);
      if (r) {
        details = `page ${r.page}, box (pdf pts): ${fmtBox(r.geometry)}`;
      }
    } else {
      const det = session?.detections.find((d) => d.id === payload.targetId);
      if (det) {
        details = `detection ${auditLabelOf(det)}`;
      }
    }
    yield* emit({
      action: payload.code
        ? `Assigned redaction code "${payload.code.label}" to ${payload.targetType}`
        : `Cleared redaction code from ${payload.targetType}`,
      details,
    });
  });
}

// ---------------------------------------------------------------------------
// Redaction-state watchers
// ---------------------------------------------------------------------------

function* watchToggleRedactionAllPages() {
  yield* takeEvery(TOGGLE_REDACTION_ALL_PAGES, function* ({ payload }) {
    const session = yield* select(selectSession);
    const r = redactionById(session?.redactions ?? [], payload.id);
    const on = !!r?.appliesToAllPages;
    yield* emit({
      action: r
        ? `${on ? 'Enabled' : 'Disabled'} stick-to-all-pages on redaction (page ${r.page})`
        : 'Toggled stick-to-all-pages on redaction',
      details: r ? `box (pdf pts): ${fmtBox(r.geometry)}` : undefined,
    });
  });
}

function* watchSetRedactionsRedacted() {
  yield* takeEvery(SET_REDACTIONS_REDACTED, function* ({ payload }) {
    if (payload.ids.length === 0) {
      return;
    }
    yield* emit({
      action: payload.redacted
        ? `Re-selected ${payload.ids.length} redaction(s)`
        : `Unredact ${payload.ids.length} redaction(s)`,
    });
  });
}

// ---------------------------------------------------------------------------
// Bates numbering (VE-24447)
// ---------------------------------------------------------------------------

/**
 * Whether two stamps are the same sequence. `JSON.stringify` matches how the
 * reducer decides an unchanged commit is a no-op, so the log and the document
 * agree on what counts as a change.
 */
const sameStamp = (
  a: BatesStampSettings | null,
  b: BatesStampSettings | null
) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Applying or changing the numbering — the identifier the production will be
 * cited by, so the log records the whole sequence definition rather than the
 * fact that something changed.
 *
 * Fires on the COMMIT only. The panel previews every keystroke against a
 * separate slice field that never reaches these actions, so an operator trying
 * placements out produces no events at all — the reason save logging had to be
 * switched off does not arise here.
 */
export function* onSetBatesStamp(action: ReturnType<typeof SET_BATES_STAMP>) {
  const session = yield* select(selectSession);
  const next = session?.batesStamp ?? null;
  if (!next) {
    return;
  }
  // The pre-commit stamp rides ON THE ACTION, read from live state at the
  // dispatch site. Nothing here remembers it: the reducer no-ops an unchanged
  // commit, so no state afterwards distinguishes "applied" from "nothing
  // happened", and anything this saga cached would go stale the moment an undo
  // changed the stamp without passing through here.
  const prev = action.payload.previous;
  if (sameStamp(prev, next)) {
    return;
  }
  yield* emit({
    action: prev ? 'Updated Bates stamping' : 'Applied Bates stamping',
    details: describeBatesStamp(next),
  });
}

export function* onClearBatesStamp(
  action: ReturnType<typeof CLEAR_BATES_STAMP>
) {
  const session = yield* select(selectSession);
  const prev = action.payload.previous;
  // Guarded like the reducer: clearing a document that had no stamp is a no-op,
  // and an audit trail should not record one.
  if (!prev || session?.batesStamp) {
    return;
  }
  yield* emit({
    action: 'Removed Bates stamping',
    details: describeBatesStamp(prev),
  });
}

function* watchSetBatesStamp() {
  yield* takeEvery(SET_BATES_STAMP, onSetBatesStamp);
}

function* watchClearBatesStamp() {
  yield* takeEvery(CLEAR_BATES_STAMP, onClearBatesStamp);
}

// ---------------------------------------------------------------------------
// Lifecycle watchers
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Custom lexicons (VE-24948 / U5, `U5-FR16`)
//
// NFR-S7 governs every string below: LEXICON NAME ONLY, never a term, never an
// entry count broken down by term, never a matched phrase. What the trail must
// answer is "which lexicon was applied to this document, by whom, when, and did
// it complete" — none of which needs the term list.
//
// Names come from the applied RECORD (`AppliedLexiconRecord.lexiconName`),
// captured at application time, not from a live lookup: a lexicon renamed or
// deleted afterwards must not change what the audit trail says happened
// (`U5-FR2`).
// ---------------------------------------------------------------------------

function* watchLexiconApplyRequested() {
  yield* takeEvery(APPLY_LEXICONS_TO_OPEN_DOCUMENT, function* ({ payload }) {
    if (payload.lexiconIds.length === 0) {
      return;
    }
    // Logged as REQUESTED, not applied. The outcome is a separate event below,
    // so a run that fails or is cut short by a budget cannot read back as a
    // completed application — the same reason ADR-003 splits intent from
    // outcome for lexicon mutations.
    yield* emit({
      action:
        payload.source === 'upload'
          ? `Lexicon application from upload selection started (${payload.lexiconIds.length} lexicon(s))`
          : `Lexicon application requested (${payload.lexiconIds.length} lexicon(s))`,
    });
  });
}

/**
 * True when `requestId` names the run the session currently holds.
 *
 * The apply reducer guards every case on `requestId` because runs are
 * cancellable and a terminated worker's in-flight message can still be
 * delivered. The audit watchers had no such guard, so a superseded run could
 * write an outcome into the disclosure-facing trail for an event that never
 * reached state (review-gate M7).
 */
function* isCurrentApplyRun(requestId: string) {
  const apply = yield* select(selectLexiconApply);
  return apply?.requestId === requestId;
}

function* watchLexiconApplied() {
  yield* takeEvery(LEXICON_APPLY_SUCCESS, function* ({ payload }) {
    if (payload.appliedLexicons.length === 0) {
      return;
    }
    // Same `requestId` guard the reducer applies to this action. Four
    // independent watchers start runs, so concurrent runs are routine and a
    // superseded one's outcome must not reach the disclosure-facing audit
    // trail — the reducer already refuses to apply it to state (M7).
    if (!(yield* isCurrentApplyRun(payload.requestId))) {
      return;
    }
    // Re-evaluation fires on every page-text arrival, so a long document
    // produces many runs that add nothing. Logging each as an application made
    // one user action read as N separate applications in the disclosure-facing
    // audit CSV (review finding 7).
    const completed = payload.completedIndexing ?? [];
    // A no-op run that did NOT finish still leaves a record the trail must
    // match, or its last word would be an earlier, clean-looking apply.
    const unfinished = runLeftWorkUnfinished(payload);
    if (!payload.changed && completed.length === 0 && !unfinished) {
      return;
    }
    // `name (rNN)` so the trail records WHICH revision was applied — the whole
    // point of BR-51's per-record `revision`, and what lets a reviewer tell a
    // v3 application apart from a v5 one.
    const names = payload.appliedLexicons
      .filter(
        (r) =>
          payload.changed ||
          completed.length === 0 ||
          completed.includes(r.lexiconId)
      )
      .map((r) => `"${r.lexiconName}" (r${r.revision})`)
      .join(', ');
    const source = payload.appliedLexicons[0]?.source ?? 'editor';
    const details: string[] = [
      `${payload.matchCount} match(es) across ${payload.pagesMatched.length} page(s)`,
    ];
    // BR-47 — an abandoned entry is named to the USER by id in the panel; the
    // audit trail records only that some were abandoned. The entry id is a
    // stable key, not a term, but it is still lexicon content and the CSV does
    // not need it to answer "did this application complete".
    if (payload.abandonedEntryIds.length > 0) {
      details.push(
        `${payload.abandonedEntryIds.length} entry/entries not evaluated`
      );
    }
    if (payload.timedOut) {
      details.push('stopped early by the matching time budget');
    }
    // Found but not placeable: a match the reviewer never sees a card for.
    // `matchCount` counts only what was placed, so without this the trail
    // read "0 match(es)" over text that matched (VE-29686).
    const unplaced = payload.disposition?.unresolvedGeometry ?? 0;
    if (unplaced > 0) {
      details.push(`${unplaced} match(es) could not be placed`);
    }
    // A dead worker is recorded as a dead worker (review-gate #3 BR-12).
    // Writing it as a time-budget event put a DEPLOYMENT fault into a
    // legal-release audit trail as a performance note — the worker chunk
    // failing to load is a documented prod-only breakage point here — and left
    // an operator reading "slow" when the answer was "broken".
    if (payload.crashed) {
      details.push('the matching worker failed before finishing');
    }
    // Which pages went unsearched, so the trail says what was NOT covered
    // rather than only that something was missed (review-gate #3 BR-11).
    // Page numbers only — never text, never terms (NFR-S7 / SEC-R3).
    if (payload.unsearchedPages && payload.unsearchedPages.length > 0) {
      details.push(
        `page(s) not searched: ${formatPageRanges(payload.unsearchedPages)}`
      );
    }
    // The pages missed cannot be listed, so say THAT, or a run over a document
    // indexed to page 1 of 1,602 reads as a clean apply (VE-29686).
    if (payload.pageCountUnknown) {
      details.push(
        'the document text index failed before its page count was known; pages beyond those indexed were not searched'
      );
    } else if (payload.indexFailed) {
      // The pages ARE listed above, but naming them is not naming the CAUSE.
      // Without this the common shape — an index that died part-way — logged
      // "page(s) not searched: 301-1602" and nothing else, which an operator
      // reads as slowness when the answer is a broken index (BR-12). The
      // rarer unknown-count case above was already named; this one was not.
      details.push(
        'the document text index failed, so those pages will not be filled in by re-applying; reopen the document to rebuild it'
      );
    }
    if (payload.changed && completed.length > 0) {
      details.push('follows up the evaluation made before indexing finished');
    }
    if (payload.indexing) {
      details.push(
        'evaluated before the document finished indexing; the remaining pages are searched when it does'
      );
    }
    yield* emit({
      action: !payload.changed
        ? completed.length > 0
          ? `Lexicon application re-run after indexing: ${names}`
          : `Lexicon application re-run: ${names}`
        : source === 'upload'
          ? `Applied lexicon(s) selected at upload: ${names}`
          : `Applied lexicon(s): ${names}`,
      details: details.join('; '),
    });
  });
}

function* watchLexiconApplyFailed() {
  yield* takeEvery(LEXICON_APPLY_FAILURE, function* ({ payload }) {
    // See `watchLexiconApplied` — a superseded run's failure recorded
    // "Lexicon application failed" against a run that changed no state and
    // showed nothing to the user (M7).
    if (!(yield* isCurrentApplyRun(payload.requestId))) {
      return;
    }
    // The failure message is deliberately NOT included: it can originate in a
    // regex engine and is not guaranteed to be free of pattern text
    // (SEC-R3 / NFR-S7). "It failed" is what the trail needs.
    yield* emit({ action: 'Lexicon application failed' });
  });
}

export function* onDocumentExportRequested() {
  {
    const session = yield* select(selectSession);
    const bates = session?.batesStamp ?? null;
    // The RANGE, which only this moment knows: the sequence definition says
    // where it starts, but which pages survive the export decides the first
    // and last numbers actually stamped. Every page carries the number of its
    // ORIGINAL position, so only deleting the first or last page
    // moves an end — a deleted page in between is a gap inside the range.
    //
    // Through `batesRangeForExport`, which reads the same
    // `survivingOriginalPages` the worker stamps from: one pass over the pages,
    // bounded to the real page range so a duplicate or out-of-range entry in
    // `excludedPages` cannot move the production's first or last number. (The
    // earlier per-page walk was O(pages² × excluded) on the main thread, at the
    // moment the operator clicks Redact File.)
    let details: string | undefined;
    if (bates && session) {
      const range = batesRangeForExport(
        bates.startNumber,
        session.totalPages,
        session.excludedPages
      );
      details =
        range === null
          ? `Bates stamping: ${describeBatesStamp(bates)}`
          : `Bates ${formatBatesLabel(bates, range.first)}–${formatBatesLabel(bates, range.last)}`;
    }
    yield* emit({
      action: 'Document redaction export requested',
      details,
    });
  }
}

function* watchDocumentExportRequested() {
  yield* takeEvery(EXPORT_REQUEST, onDocumentExportRequested);
}

function* watchDocumentSaved() {
  yield* takeEvery(SAVE_SUCCESS, function* (action) {
    if (!AUDIT_LOG_SAVES) {
      return;
    }
    // VE-28061: the export's pre-burn-in save outlives the editor, so this can
    // fire for a document that is no longer the open one. `emit` attributes the
    // event to the OPEN session, so emitting here would log "Document saved"
    // against the wrong document's audit trail.
    const session = yield* select(selectSession);
    if (session?.tdoId !== action.payload.tdoId) {
      return;
    }
    yield* emit({ action: 'Document saved' });
  });
}

// ---------------------------------------------------------------------------
// License metering (see ../licenseMeter.ts)
// ---------------------------------------------------------------------------

/**
 * One meter event per document opened WITH a PDF: that is the browser-side
 * unit of "document processed" under the MuPDF license. A session without a
 * PDF URL renders the "no PDF" state and MuPDF never runs, so nothing is
 * recorded. Bypasses `emit` because the payload carries a tag key, not just
 * `details`. Exported for tests; `watchDocumentOpened` wires it up.
 */
export function* onDocumentOpenedForLicenseMeter({
  payload,
}: ReturnType<typeof OPEN_DOCUMENT_SUCCESS>) {
  if (!payload.pdfUrl) {
    return;
  }
  yield* put(
    LOG_AUDIT_EVENT({
      action: LICENSE_METER_ACTION,
      payload: licenseMeterPayload(),
      // Name the document explicitly rather than relying on the live session:
      // the meter must be filed against the document MuPDF just opened even if
      // a document switch lands before the emitter runs.
      tdoId: payload.tdoId,
    })
  );
}

function* watchDocumentOpened() {
  yield* takeEvery(OPEN_DOCUMENT_SUCCESS, onDocumentOpenedForLicenseMeter);
}
